import React from 'react';
import { Column } from 'react-table';
import { DailySummaryItem } from '../types';
import { formatDate } from '../../../../utilities/dateHelpers';
import './DailySummaryTable.scss';


const fmtNumber = (v: number | string | null | undefined, digits = 2): string | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  if (Number.isNaN(n)) return null;
  return n.toFixed(digits);
};

const MutedCell: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <span className="cell-muted">{children ?? '-'}</span>
);

export const dailySummaryColumns = (tier: 1 | 2 | null): Column<DailySummaryItem>[] => {
  const globalTierIs1 = tier === 1;

  const renderLostColumn = ({ value, row }: any): JSX.Element => {
    const rowTier = row?.original?._tier as 1 | 2 | null | undefined;
    const isTier1 = globalTierIs1 || rowTier === 1;

    // Tier 1 -> muted/greyed cell (show '-' or muted content)
    if (isTier1) {
      return <MutedCell />;
    }

    // Tier 2 -> show numeric value. If null/invalid => show 0.00 (per request)
    const formatted = fmtNumber(value);
    if (formatted !== null) return <span>{formatted}</span>;
    return <span>{Number(0).toFixed(2)}</span>;
  };

  return [
    {
      Header: 'Date',
      accessor: 'date',
      id: 'date',
      Cell: ({ value }: any) => (value ? <span>{formatDate(value)}</span> : <MutedCell />),
    },
    {
      Header: 'Hours',
      accessor: 'hours',
      id: 'hours',
      Cell: ({ value }: any) =>
        value === null || value === undefined ? <MutedCell /> : <span>{Number(value).toFixed(2)}</span>,
    },
    {
      Header: 'MOP',
      accessor: 'mop',
      id: 'mop',
      Cell: ({ value, row }: any) => {
        const rowTier = row?.original?._tier as 1 | 2 | null | undefined;
        const isTier1 = globalTierIs1 || rowTier === 1;
        if (isTier1) return <MutedCell />;
        return <span>{value ?? 'ESP'}</span>;
      },
    },
    {
      Header: 'Lost oil (stb)',
      accessor: 'lostOil',
      id: 'lostOil',
      Cell: renderLostColumn,
    },
    {
      Header: 'Lost gas',
      accessor: 'lostGas',
      id: 'lostGas',
      Cell: renderLostColumn,
    },
    {
      Header: 'Lost water',
      accessor: 'lostWater',
      id: 'lostWater',
      Cell: renderLostColumn,
    },
    {
      Header: 'Downtime %',
      accessor: 'downtimePercent',
      id: 'downtimePercent',
      Cell: ({ value }: any) =>
        value === null || value === undefined ? <MutedCell /> : <span>{Number(value).toFixed(2)}</span>,
    },
    {
      Header: 'Downtime events',
      accessor: 'downtimeEvents',
      id: 'downtimeEvents',
      Cell: ({ value }: any) => (value === null || value === undefined ? <MutedCell /> : <span>{String(value)}</span>),
    },
  ];
};


export const mapDailySummaryToTableData = (items: any[] | any): DailySummaryItem[] => {
  if (!items) return [];

  // If entire response object passed (Tier1 style), extract shutdownDailySummary
  if (!Array.isArray(items) && typeof items === 'object' && items.shutdownDailySummary) {
    items = items.shutdownDailySummary;
  }

  if (!Array.isArray(items)) return [];

  if (items.length === 0) return [];

  const first = items[0];


  if (first && (first.date !== undefined || first.hours !== undefined) && !(first.Date || first.Hours)) {
    return (items as DailySummaryItem[]).map((it) => ({
      ...it,
      mop: (it as any).mop ?? (it as any).MethodOfProduction ?? (it as any).MethodOfProductionDescription ?? 'ESP',
    }));
  }

  // Map PumpChecker / Tier2 style rows defensively
  return items.map((it: any) => {
    const date = it.Date ?? it.date ?? it.DateString ?? null;
    const hours = it.Hours ?? it.hours ?? null;
    const downtimePercent = it.DowntimePercentage ?? it.downtimePercent ?? it.downtimePercentage ?? null;
    const downtimeEvents =
      it.NoOfDowntimes ?? it.noOfDowntimes ?? it.downtimeEvents ?? it.NoOfDowntimes ?? null;

    const lostOilRaw = it.LostOil ?? it.lostOil ?? null;
    const lostGasRaw = it.LostGas ?? it.lostGas ?? null;
    const lostWaterRaw = it.LostWater ?? it.lostWater ?? null;

    const mopFromDescription = it.MethodOfProductionDescription ?? it.methodOfProductionDescription ?? null;
    const mopFromWellField = it.WellMethodOfProduction ?? it.wellMethodOfProduction ?? null;
    const mopCode = it.MethodOfProduction ?? it.methodOfProduction ?? null;

    const mapMopCode = (code: number | string | null): string | null => {
      if (code === null || code === undefined) return null;
      if (!isNaN(Number(code))) {
        const n = Number(code);
        if (n === 2) return 'ESP';
        if (n === 1) return 'Unknown';
      }
      return String(code);
    };

    const mop = mopFromDescription || mopFromWellField || (mopCode !== null ? mapMopCode(mopCode) : null) || null;

    const item: DailySummaryItem = {
      date,
      hours: hours != null ? Number(hours) : null,
      downtimePercent: downtimePercent != null ? Number(downtimePercent) : null,
      downtimeEvents: downtimeEvents != null ? Number(downtimeEvents) : null,
      lostOil: lostOilRaw != null && !Number.isNaN(Number(lostOilRaw)) ? Number(lostOilRaw) : null,
      lostGas: lostGasRaw != null && !Number.isNaN(Number(lostGasRaw)) ? Number(lostGasRaw) : null,
      lostWater: lostWaterRaw != null && !Number.isNaN(Number(lostWaterRaw)) ? Number(lostWaterRaw) : null,
      mop: mop ?? 'ESP', // default to 'ESP' when missing
      _raw: it,
      _tier: 2 as 1 | 2, // treat pumpchecker rows as Tier2 by default
    };

    return item;
  });
};

export default dailySummaryColumns;
